import * as React from 'react';
import styled from 'styled-components';
import {
  Container as LastTransactionsContainer,
  Header,
  Title,
} from './LastTransactions';
import StatisticSummary from './StatisticSummary';
import Checkbox from 'components/Checkbox';
import {DashboardProps} from '../index';
import {
  ResponsiveContainer,
  LineChart,
  XAxis,
  YAxis,
  Line,
  Tooltip,
  CartesianGrid, TooltipProps,
} from 'recharts';
import GraphTooltip, {GraphTooltipItem} from './GraphTooltip';

export type StudentsProps = Pick<DashboardProps, 'students'>

export enum LineKey {
  All='all',
  New='new',
  Active='active',
}

const Container = styled(LastTransactionsContainer)`
  max-width: none;
`;

export const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr max-content;
  gap: 20px;
  padding: 20px;
`;

export const Statistic = styled.div`
  display: grid;
  gap: 10px;
`;

export const GraphBox = styled.div`
  display: grid;
  gap: 20px;
`;

export const Graph = styled.div`
  height: 228px;
  //background-color: #232323;
  border-radius: 3px;
  
  tspan {
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 13px;
    letter-spacing: 0.01em;
    text-align: center;
    fill: white;
    opacity: .4;
  }
`;

export const GraphFilters = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 20px;
`;

/**
 * The function render Category item
 * @param {StudentsProps} props
 * @constructor
 */
export default function Students(props: StudentsProps) {
  const {students} = props;

  const [currentActive, setCurrentActive] = React.useState('all');

  const colors = {
    all: '#3264EF',
    new: '#AA55F7',
    active: '#FF6DAE',
  };

  const [showedLines, setShowedLines] = React.useState({
    all: true,
    new: true,
    active: true,
  });

  /**
   * The function set showed lines
   * @param {string} key
   * @param {boolean} checked
   * @constructor
   */
  function changeShowedLines(key: LineKey, checked: boolean) {
    setShowedLines({
      ...showedLines,
      [key]: checked,
    });
  }

  const data = [
    {
      name: 'Янв',
      all: 12,
      new: 12,
      active: 0,
    },
    {
      name: 'Фев',
      all: 30,
      new: 30,
      active: 0,
    },
    {
      name: 'Мар',
      all: 320,
      new: 125,
      active: 215,
    },
    {
      name: 'Апр',
      all: 290,
      new: 10,
      active: 200,
    },
    {
      name: 'Май',
      all: 320,
      new: 25,
      active: 270,
    },
    {
      name: 'Июн',
      all: 400,
      new: 50,
      active: 300,
    },
    {
      name: 'Июл',
      all: 430,
      new: 59,
      active: 330,
    },
    {
      name: 'Авг',
      all: 450,
      new: 10,
      active: 400,
    },
    {
      name: 'Сен',
      all: 370,
      new: 12,
      active: 288,
    },
    {
      name: 'Окт',
      all: 370,
      new: 12,
      active: 288,
    },
    {
      name: 'Ноя',
      all: 370,
      new: 12,
      active: 288,
    },
    {
      name: 'Дек',
      all: 370,
      new: 12,
      active: 288,
    },
  ];

  /**
   * The function set showed lines
   * @param {TooltipProps} props
   * @constructor
   */
  function CustomTooltip(props: TooltipProps<any, any>): JSX.Element | null {
    if (props.active && props?.payload) {
      const items: GraphTooltipItem[] = [];

      showedLines.all && items.push({
        title: 'Все',
        dotColor: colors.all,
        value: props.payload[0].payload.all,
      });
      showedLines.new && items.push({
        title: 'Новые',
        dotColor: colors.new,
        value: props.payload[0].payload.new,
      });
      showedLines.active && items.push({
        title: 'Активные',
        dotColor: colors.active,
        value: props.payload[0].payload.active,
      });


      return (
        <GraphTooltip
          header={props.label}
          items={items}
        />
      );
    }

    return null;
  }

  return (
    <Container>
      <Header>
        <Title>Студенты</Title>
      </Header>
      <Content>
        <GraphBox>
          <Graph>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
              >
                <CartesianGrid vertical={false} opacity={0.1} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip content={<CustomTooltip />}/>
                <Line
                  type="monotone"
                  dataKey={LineKey.All}
                  stroke={colors.all}
                  fill={colors.all}
                  activeDot={{r: 8}}
                  hide={!showedLines.all}
                  strokeWidth={3}
                />
                <Line
                  type="monotone"
                  dataKey={LineKey.New}
                  stroke={colors.new}
                  fill={colors.new}
                  hide={!showedLines.new}
                  strokeWidth={3}
                />
                <Line
                  type="monotone"
                  dataKey={LineKey.Active}
                  stroke={colors.active}
                  fill={colors.active}
                  hide={!showedLines.active}
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </Graph>

          <GraphFilters>
            <Checkbox
              label={'все'}
              accentLabel
              colorize={colors.all}
              checked={showedLines.all}
              onChange={(e) => changeShowedLines(
                  LineKey.All,
                  e.target.checked,
              )}
            />
            <Checkbox
              label={'новые пользователи'}
              accentLabel
              colorize={colors.new}
              checked={showedLines.new}
              onChange={(e) => changeShowedLines(
                  LineKey.New,
                  e.target.checked,
              )}
            />
            <Checkbox
              label={'активные пользователи'}
              accentLabel
              colorize={colors.active}
              checked={showedLines.active}
              onChange={(e) => changeShowedLines(
                  LineKey.Active,
                  e.target.checked,
              )}
            />
          </GraphFilters>
        </GraphBox>
        <Statistic>
          <StatisticSummary
            title={'за все время'}
            value={students.activeStudents + students.newStudents}
            onClick={() => setCurrentActive('all')}
            primary={currentActive === 'all'}
          />
          <StatisticSummary
            title={'в этом месяце'}
            value={students.newStudents}
            onClick={() => setCurrentActive('month')}
            primary={currentActive === 'month'}
          />
          <StatisticSummary
            title={'активны сейчас'}
            value={students.activeStudents}
            onClick={() => setCurrentActive('nowActive')}
            primary={currentActive === 'nowActive'}
          />
        </Statistic>
      </Content>
    </Container>
  );
}
